import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Eye, EyeOff, Info } from 'lucide-react';
import { reasonToMessage } from '@/lib/authRecovery';
import predioFrontal from '@/assets/login/predio-frontal.jpg';

const authSchema = z.object({
  email: z.string().trim().email({ message: "Email inválido" }).max(255, { message: "Email deve ter menos de 255 caracteres" }),
  password: z.string().min(6, { message: "Senha deve ter pelo menos 6 caracteres" }).max(100, { message: "Senha deve ter menos de 100 caracteres" })
});

type AuthFormData = z.infer<typeof authSchema>;

const MODULOS = ['Discadora', 'WhatsApp Inbox', 'Leads & Carteira', 'Prime Tec.IA'];

export function AuthForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  // Aviso amigável quando o usuário cai aqui por sessão expirada (vem em ?reason=...)
  const [recoveryNotice, setRecoveryNotice] = useState<string | null>(null);
  const [now, setNow] = useState<Date>(() => new Date());

  const { signIn } = useAuth();

  // Lê ?reason=... da URL pra mostrar mensagem de "sessão expirou". Roda 1x.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const reason = params.get('reason');
      const msg = reasonToMessage(reason);
      if (msg) {
        setRecoveryNotice(msg);
        params.delete('reason');
        const newQuery = params.toString();
        const newUrl = window.location.pathname + (newQuery ? `?${newQuery}` : '');
        window.history.replaceState(null, '', newUrl);
      }
    } catch (_) { /* não-bloqueante */ }
  }, []);

  // Relógio vivo da coluna ambiente.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const { register, handleSubmit, formState: { errors } } = useForm<AuthFormData>({
    resolver: zodResolver(authSchema)
  });

  const onSubmit = async (data: AuthFormData) => {
    setIsLoading(true);
    setError('');
    try {
      const { error } = await signIn(data.email, data.password);
      if (error) {
        const msg = error.message || '';
        if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('fetch')) {
          setError('Servidor temporariamente indisponível. Tente novamente em alguns segundos.');
        } else {
          setError(msg);
        }
      }
    } catch (err: any) {
      const msg = err?.message || 'Erro inesperado';
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        setError('Servidor temporariamente indisponível. Tente novamente em alguns segundos.');
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const hora = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const dataStr = now.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'short' })
    .replace('.', '');

  return (
    <div className="relative min-h-screen w-full bg-background lg:overflow-hidden">
      {/* ============ FOTO DA SEDE (banner no mobile, painel à esquerda no desktop) ============ */}
      <div className="relative m-4 h-52 overflow-hidden rounded-2xl bg-[#2A0810] shadow-[0_20px_50px_-20px_rgba(42,8,16,.45)] lg:absolute lg:inset-y-7 lg:left-7 lg:m-0 lg:h-auto lg:w-[54%]">
        <img
          src={predioFrontal}
          alt="Sede Prime Telefonia Empresarial"
          className="absolute inset-0 h-full w-full object-cover object-[center_30%]"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(42,8,16,.30) 0%, rgba(42,8,16,0) 35%, rgba(26,4,10,.85) 100%)' }}
        />
        <div className="absolute inset-x-6 bottom-6 flex flex-col gap-2.5 lg:inset-x-auto lg:bottom-9 lg:left-9 lg:right-52">
          <h2 className="font-display text-2xl font-bold leading-[1.12] tracking-[-0.5px] text-white lg:text-[34px]">
            Quem atende bem,<br />vende sempre.
          </h2>
          <span className="text-xs text-white/70 lg:text-[13px]">
            Sede Prime · Porto Alegre / RS · Agente Autorizado Claro empresas
          </span>
        </div>
      </div>

      {/* ============ CARD DE LOGIN ============ */}
      <div className="relative z-10 mx-auto mb-10 w-[min(420px,calc(100%-2rem))] rounded-[20px] border border-border bg-white p-8 shadow-[0_34px_80px_-24px_rgba(42,8,16,.4)] dark:border-border dark:bg-card sm:p-10 lg:absolute lg:left-[47%] lg:top-1/2 lg:mx-0 lg:mb-0 lg:-translate-y-1/2">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold uppercase tracking-[2px] text-brand-mid dark:text-brand-rose">
            Prime Tec.IA
          </span>
          <h1 className="font-display text-[27px] font-bold tracking-[-0.3px] text-foreground">
            Bem-vindo de volta
          </h1>
          <p className="text-[13.5px] text-muted-foreground">Digite suas credenciais para continuar</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-7 flex flex-col gap-3.5">
          <div>
            <input
              type="email"
              placeholder="seu@email.com"
              autoComplete="email"
              disabled={isLoading}
              {...register('email')}
              className="h-[50px] w-full rounded-xl border-[1.5px] border-border bg-surface px-4 text-[14.5px] text-foreground outline-none transition-all placeholder:text-muted-foreground/60 focus:border-primary focus:ring-4 focus:ring-primary/10 disabled:opacity-60 dark:border-border dark:bg-foreground"
            />
            {errors.email && <p className="mt-1.5 text-xs text-destructive">{errors.email.message}</p>}
          </div>

          <div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Sua senha"
                autoComplete="current-password"
                disabled={isLoading}
                {...register('password')}
                className="h-[50px] w-full rounded-xl border-[1.5px] border-border bg-surface pl-4 pr-12 text-[14.5px] text-foreground outline-none transition-all placeholder:text-muted-foreground/60 focus:border-primary focus:ring-4 focus:ring-primary/10 disabled:opacity-60 dark:border-border dark:bg-foreground"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                disabled={isLoading}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-primary"
              >
                {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
              </button>
            </div>
            {errors.password && <p className="mt-1.5 text-xs text-destructive">{errors.password.message}</p>}
          </div>

          {recoveryNotice && !error && (
            <Alert className="border-warning/40 bg-warning/5">
              <Info className="h-4 w-4 text-warning" />
              <AlertDescription className="text-warning">{recoveryNotice}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="mt-1 flex h-[52px] w-full items-center justify-center rounded-xl bg-gradient-to-br from-brand-vinho to-primary text-[15px] font-bold text-white shadow-[0_10px_24px_-8px_rgba(91,10,13,.55)] transition-transform hover:-translate-y-px disabled:opacity-70 dark:from-[#9D2530] dark:to-[#6C2236]"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Entrar no Sistema
          </button>
        </form>

        <div className="mt-6 flex items-center justify-between border-t border-border pt-4 dark:border-border">
          <a
            href="/esqueci-senha"
            className="text-[12.5px] font-semibold text-muted-foreground transition-colors hover:text-primary"
          >
            Esqueci minha senha
          </a>
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-success" />
            <span className="text-xs font-semibold text-muted-foreground">Online</span>
          </div>
        </div>
      </div>

      {/* ============ COLUNA AMBIENTE (só desktop) ============ */}
      <div className="hidden lg:block">
        <div className="absolute right-11 top-11 flex flex-col items-end gap-0.5">
          <span className="font-display text-[26px] font-bold tabular-nums text-foreground">{hora}</span>
          <span className="text-[12.5px] capitalize text-muted-foreground">Porto Alegre · {dataStr}</span>
        </div>

        <div className="absolute right-11 top-48 flex w-48 flex-col gap-3.5">
          <span className="text-[11px] font-bold uppercase tracking-[1.8px] text-brand-mid dark:text-brand-rose">
            No sistema
          </span>
          {MODULOS.map((m) => (
            <div key={m} className="flex items-center gap-2.5">
              <span className="h-[7px] w-[7px] rounded-full bg-success" />
              <span className="text-[13px] font-semibold text-muted-foreground">{m}</span>
            </div>
          ))}
          <div className="mt-1 h-px w-11 bg-border dark:bg-border" />
          <span className="text-xs leading-relaxed text-muted-foreground">Tudo operando normalmente nesta manhã.</span>
        </div>

        <div className="absolute bottom-10 right-11 flex flex-col items-end gap-1">
          <span className="text-xs text-muted-foreground">beta.crm.primetecia.com.br</span>
          <span className="text-xs text-muted-foreground/70">Sistema Integrado de Gestão Inteligente</span>
        </div>
      </div>
    </div>
  );
}
